/**
 * PWA infrastructure — service worker registration + install prompt UI.
 *
 * Exports:
 *   - registerServiceWorker(): call once at app boot (idempotent)
 *   - InstallPWAPrompt: small dismissible banner that surfaces the OS install dialog
 *
 * Why a custom prompt instead of relying on Chrome's mini-infobar?
 * Chrome's native infobar was removed on desktop and is invisible on mobile
 * until the user "engages". A polite custom CTA gives us control over the
 * messaging + dismiss state.
 */

import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

export const registerServiceWorker = () => {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  // Don't register in dev (hot reload + SW is a debugging nightmare)
  if (process.env.NODE_ENV !== 'production' && !window.__FORCE_SW__) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/' })
      .then((reg) => {
        // Listen for updates and prompt the user (in production a toast would
        // be nicer; for now we just silently activate the new SW on next load)
        reg.addEventListener('updatefound', () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener('statechange', () => {
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
              // New SW is waiting — let it activate automatically next visit
              // (we don't force-reload to avoid disrupting an active session)
            }
          });
        });
      })
      .catch(() => {
        // SW registration failures shouldn't break the app
      });
  });
};

const DISMISSED_KEY = 'pwa_install_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export const InstallPWAPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [iosBannerVisible, setIosBannerVisible] = useState(false);

  useEffect(() => {
    // Already installed (running as standalone)?
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setInstalled(true);
      return;
    }

    const dismissedAt = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10);
    const recentlyDismissed = dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS;
    if (recentlyDismissed) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setInstalled(true);
      setVisible(false);
    };
    window.addEventListener('appinstalled', installedHandler);

    // iOS Safari: no beforeinstallprompt event. Show an explicit "Add to
    // Home Screen" hint after a short delay so users on iPhones aren't
    // left out. Only on Safari, not Chrome-on-iOS (both report iPhone UA).
    const ua = window.navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
    if (isIOS && isSafari) {
      const t = setTimeout(() => setIosBannerVisible(true), 6000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', handler);
        window.removeEventListener('appinstalled', installedHandler);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (e) {}
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
    setIosBannerVisible(false);
  };

  if (installed) return null;

  // Standard PWA install (Android Chrome, desktop Chrome/Edge)
  if (visible && deferredPrompt) {
    return (
      <div
        className="fixed bottom-4 inset-x-4 md:left-auto md:right-4 md:max-w-sm z-50 bg-card border border-primary/40 rounded-md shadow-2xl p-3 flex items-center gap-3"
        data-testid="pwa-install-prompt"
      >
        <div className="w-10 h-10 rounded-md bg-primary/15 flex items-center justify-center shrink-0">
          <Download size={18} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Install Meybra</p>
          <p className="text-xs text-muted-foreground">One tap from your home screen.</p>
        </div>
        <button
          onClick={handleInstall}
          className="bg-primary text-black px-3 py-1.5 rounded text-xs font-bold hover:bg-primary/90 shrink-0"
          data-testid="pwa-install-btn"
        >
          Install
        </button>
        <button
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground p-1 shrink-0"
          data-testid="pwa-install-dismiss"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  // iOS Safari: show the manual "Add to Home Screen" hint
  if (iosBannerVisible) {
    return (
      <div
        className="fixed bottom-4 inset-x-4 z-50 bg-card border border-primary/40 rounded-md shadow-2xl p-3"
        data-testid="pwa-ios-prompt"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-primary/15 flex items-center justify-center shrink-0">
            <Download size={18} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Install Meybra on iPhone</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Tap <span className="inline-block px-1.5 py-0.5 rounded bg-muted text-foreground font-bold">Share ⎙</span> then{' '}
              <span className="inline-block px-1.5 py-0.5 rounded bg-muted text-foreground font-bold">Add to Home Screen</span>.
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground p-1 shrink-0"
            data-testid="pwa-ios-dismiss"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPWAPrompt;
